import { render } from 'preact';
import { createPreviewApiClient, createWidgetApiClient, registerWidgetApiClient } from './lib/api';
import { cacheDisabled, isDisabledCached } from './lib/storage';
import type { Template } from './templates/types';
import type { WidgetConfig } from './lib/types';
import { MinimalConfigResponseSchema, ConfigResponseSchema } from './lib/types';

type TemplateLoader = () => Promise<{ default: Template }>;

const TEMPLATE_LOADERS: Record<string, TemplateLoader> = {
  'classic-bubble': () => import('./templates/classic-bubble'),
  'slide-in-sidebar': () => import('./templates/slide-in-sidebar'),
  'bottom-bar': () => import('./templates/bottom-bar'),
  'greeting-card': () => import('./templates/greeting-card'),
  'glassmorphic-float': () => import('./templates/glassmorphic-float'),
  'minimal-dock': () => import('./templates/minimal-dock'),
};

/**
 * Preview mode: the widget is embedded in the CRM dashboard in an iframe.
 * Config updates arrive via postMessage from the parent frame.
 */
/**
 * Resolve the trusted origin for preview postMessage communication.
 *
 * The parent frame is always the CRM dashboard that embedded us.
 * We derive its origin from document.referrer (set by the browser) or, when
 * same-origin, from window.parent.location.  If neither source is available
 * the preview script should not have been injected, so we return null and
 * skip all message handling — this prevents accepting messages from
 * arbitrary origins.
 */
function resolvePreviewParentOrigin(): string | null {
  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch {
    // Unparseable referrer — fall through
  }
  try {
    // Same-origin parent: window.parent.location is accessible
    return window.parent.location.origin;
  } catch {
    // Cross-origin parent with no referrer — cannot establish a trusted origin
    return null;
  }
}

function initPreview(): void {
  const trustedOrigin = resolvePreviewParentOrigin();
  if (!trustedOrigin) {
    // Cannot determine who the parent is — refuse to operate to avoid
    // accepting messages from untrusted origins.
    return;
  }

  const container = document.createElement('div');
  container.id = 'ch-widget-container';
  container.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;z-index:2147483647;pointer-events:none;overflow:visible;';
  document.body.appendChild(container);
  const shadow = container.attachShadow({ mode: 'open' });
  const mountPoint = document.createElement('div');
  shadow.appendChild(mountPoint);

  let cachedApp: typeof import('./components/App')['App'] | null = null;
  const templateCache: Record<string, Template> = {};
  let previewClientRegistered = false;

  window.addEventListener('message', async (e: MessageEvent) => {
    // Reject messages from any origin other than the trusted parent
    if (e.origin !== trustedOrigin) return;
    if (e.source !== window.parent) return;
    if ((e.data as Record<string, unknown>)?.type !== 'ch-preview') return;

    const evData = e.data as { type: string; config: WidgetConfig; siteId?: string };
    const previewConfig = evData.config;
    const siteId = evData.siteId || 'preview';
    const templateId = previewConfig.template || 'classic-bubble';

    if (!templateCache[templateId]) {
      const loader = TEMPLATE_LOADERS[templateId] ?? TEMPLATE_LOADERS['classic-bubble'];
      // Non-null assertion: TEMPLATE_LOADERS['classic-bubble'] always exists
      const mod = await (loader ?? TEMPLATE_LOADERS['classic-bubble']!)();
      templateCache[templateId] = mod.default;
    }

    if (!cachedApp) {
      const mod = await import('./components/App');
      cachedApp = mod.App;
    }

    if (!previewClientRegistered) {
      registerWidgetApiClient(siteId, createPreviewApiClient());
      previewClientRegistered = true;
    }

    const AppComponent = cachedApp;
    render(
      <AppComponent
        siteId={siteId}
        apiBase=""
        template={templateCache[templateId]!}
        initialConfig={{ config: previewConfig, token: 'preview' }}
        isPreview={true}
      />,
      mountPoint,
    );
  });

  // Signal ready to the trusted parent only
  window.parent.postMessage({ type: 'ch-preview-ready' }, trustedOrigin);
}

async function init(): Promise<void> {
  // Preview mode check
  const previewScripts = document.querySelectorAll('script[data-preview]');
  if (previewScripts.length > 0) {
    initPreview();
    return;
  }

  const scripts = document.querySelectorAll('script[data-site-id]');
  const scriptTag = scripts[scripts.length - 1];
  if (!scriptTag) return;

  const siteId = scriptTag.getAttribute('data-site-id');
  if (!siteId) return;

  // Short-circuit if we recently saw enabled:false for this site
  if (isDisabledCached(siteId)) return;

  // Remove existing widget if present (handles script re-injection)
  const existing = document.getElementById('ch-widget-container');
  if (existing) existing.remove();

  // Detect API base: explicit attribute first, script-src inference as dev fallback
  const explicitBase = scriptTag.getAttribute('data-api-base');
  let apiBase: string;
  if (explicitBase) {
    apiBase = explicitBase.replace(/\/+$/, '');
  } else {
    const src = scriptTag.getAttribute('src') || '';
    try {
      const url = new URL(src, window.location.href);
      apiBase = `${url.protocol}//${url.hostname}:8080`;
    } catch {
      apiBase = 'http://localhost:8080';
    }
  }

  // Fetch config lazily — validate with zod at the boundary
  let rawConfig: unknown;
  try {
    const res = await fetch(`${apiBase}/v1/sites/${encodeURIComponent(siteId)}/widget/config`, {
      credentials: 'omit',
    });
    if (!res.ok) {
      // Config unavailable — exit silently
      return;
    }
    rawConfig = await res.json();
  } catch {
    // Network error — exit silently, no widget
    return;
  }

  // Step 1: minimal parse — only checks `enabled`; succeeds even when the
  // backend returns the bare {"enabled": false} disabled-site response.
  let minimal: ReturnType<typeof MinimalConfigResponseSchema.parse>;
  try {
    minimal = MinimalConfigResponseSchema.parse(rawConfig);
  } catch {
    // Unparseable response — exit silently
    return;
  }

  if (!minimal.enabled) {
    cacheDisabled(siteId);
    return;
  }

  // Step 2: full parse — only reached when enabled:true, so session_token and
  // name are guaranteed present by the backend contract.
  let parsedConfig: ReturnType<typeof ConfigResponseSchema.parse>;
  try {
    parsedConfig = ConfigResponseSchema.parse(rawConfig);
  } catch {
    // Malformed enabled-config — exit silently
    return;
  }

  const sessionToken = parsedConfig.session_token;
  const widgetConfig: WidgetConfig = {
    name: parsedConfig.name,
    logo_url: parsedConfig.logo_url,
    message_avatar_url: parsedConfig.message_avatar_url,
    accent_color: parsedConfig.accent_color,
    position: parsedConfig.position,
    welcome_message: parsedConfig.welcome_message,
    suggested_questions: parsedConfig.suggested_questions,
    template: parsedConfig.template,
    turnstile_site_key: parsedConfig.turnstile_site_key,
    turnstile_max_skips: parsedConfig.turnstile_max_skips,
    lead_capture: parsedConfig.lead_capture,
  };

  const templateId = widgetConfig.template || 'classic-bubble';
  const loader = TEMPLATE_LOADERS[templateId] ?? TEMPLATE_LOADERS['classic-bubble'];

  let template: Template;
  try {
    // Non-null: loader is always defined (fallback above ensures it)
    const mod = await loader!();
    template = mod.default;
  } catch {
    const fallback = await TEMPLATE_LOADERS['classic-bubble']!();
    template = fallback.default;
  }

  // Register the API client keyed to this siteId
  const apiClient = createWidgetApiClient({ apiBase });
  registerWidgetApiClient(siteId, apiClient);

  // Build the Shadow DOM host
  const container = document.createElement('div');
  container.id = 'ch-widget-container';
  container.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;z-index:2147483647;pointer-events:none;overflow:visible;';
  document.body.appendChild(container);

  const shadow = container.attachShadow({ mode: 'open' });
  const mountPoint = document.createElement('div');
  shadow.appendChild(mountPoint);

  const { App } = await import('./components/App');
  render(
    <App
      siteId={siteId}
      apiBase={apiBase}
      template={template}
      initialConfig={{ config: widgetConfig, token: sessionToken }}
    />,
    mountPoint,
  );
}

// Defer init until after page load to avoid CWV impact
function scheduleInit(): void {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => { void init(); });
  } else {
    setTimeout(() => { void init(); }, 0);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleInit);
} else {
  scheduleInit();
}
